import React, { Suspense } from "react";
import { Redirect } from "react-router-dom";

import ClientDashboardAppPage from "../../../uiMigration/ClientDashboardAppPage";
import * as FAC from "./featureAccessControl";
import { Spinner } from "../../../components";
import { PageFilters } from "../layout/PageFilters";
import UsePageTitle from "../layout/UsePageTitle";
import ManageTemplatesList from "../../admin/manageTemplates/ManageTemplatesList";
import LocalPagesUploader from "../../admin/manageTemplates/LocalPagesUploader";
import ChangeLogsBrowser from "../../admin/changeLogs/ChangeLogsBrowser";
import EmailAlertsPage from '../../emails/EmailAlertsPage';

/** @type {PermissionedRouteNode[]} */
export default [
  {
    path: "/snapshot",
    component: loadableWithFilters(React.lazy(() => import("../../snapshot/Snapshot"))),
    isAccessible: FAC.snapshotAllowed
  },
  {
    path: "/reviews/summary",
    component: loadableWithFilters(React.lazy(() => import("../../reviews/pages/ReviewsSummaryPage"))),
    isAccessible: FAC.reviewsAllowed
  },
  {
    path: "/reviews/storeComparison",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/reviews/pages/ReviewStoreComparisonPage"))),
    isAccessible: FAC.reviewsStoreComparisonAllowed
  },
  {
    path: "/reviews",
    component: loadableWithFilters(React.lazy(() => import("../../reviews/pages/ReviewListPage"))),
    isAccessible: FAC.reviewsAllowed,
    exact: true,
    drillInPages: [
      {
        path: "/reviews/details",
        component: loadable(React.lazy(() => import("../../reviews/pages/SingleReviewPage"))),
      }
    ]
  },
  //////////////////
  // start pulse //
  {
    path: "/pulse/summary",
    component: loadableWithFilters(React.lazy(() => import("../../pulse/SentimentOverviewPage"))),
    isAccessible: FAC.sentimentAllowed
  },
  {
    path: "/pulse/explorer",
    component: loadableWithFilters(React.lazy(() => import("../../pulse/SentimentExplorerPage"))),
    isAccessible: FAC.sentimentAllowed
  },
  {
    path: "/pulse/storeComparison",
    component: loadableWithFilters(React.lazy(() => import("../../pulse/SentimentStoreComparisonPage"))),
    isAccessible: FAC.sentimentStoreComparisonAllowed
  },
  //////////////////
  // start social //
  {
    path: "/social/summary/metrics",
    component: loadableWithFilters(React.lazy(() => import("../../social/pages/MetricsPage"))),
    isAccessible: FAC.socialAllowed,
    eventName: "Social Summary metrics"
  },
  {
    path: "/social/summary/topPerformingPosts",
    component: loadableWithFilters(React.lazy(() => import("../../social/pages/TopPerformingPostsPage"))),
    isAccessible: FAC.socialEngagementAllowed,
    eventName: "Social Summary topPerformingPosts"
  },
  {
    path: "/social/summary/providerBreakdown",
    component: loadableWithFilters(React.lazy(() => import("../../social/pages/ProviderBreakdownPage"))),
    isAccessible: FAC.socialEngagementAllowed,
    eventName: "Social Summary providerBreakdown"
  },
  {
    path: "/social/activity",
    component: loadableWithFilters(React.lazy(() => import("../../social/pages/SocialActivityPage"))),
    isAccessible: FAC.socialAllowed,
  },
  {
    path: "/social/hashtagMetrics",
    component: loadableWithFilters(React.lazy(() => import("../../social/pages/SocialHashtagKeywordPage"))),
    isAccessible: FAC.socialAllowed,
  },
  {
    path: "/social/approvals",
    component: loadableWithFilters(React.lazy(() => import("../../social/pages/PostReplyApprovalPage"))),
    isAccessible: FAC.socialCommentingAllowed,
  },
  {
    path: "/social/publishing",
    component: loadableWithFilters(React.lazy(() => import("../../social/pages/SocialPublishingPage"))),
    isAccessible: FAC.socialPublishingAllowed,
  },
  {
    path: "/social/reports",
    component: loadable(React.lazy(() => import("../../social/pages/SocialStoreComparisonReportPage"))),
    isAccessible: FAC.socialReportsAllowed,
  },
  {
    path: "/social/settings",
    component: loadable(React.lazy(() => import("../../social/pages/SocialSettingsPage"))),
    isAccessible: FAC.socialSettingsAllowed,
  },
  // end social //
  ////////////////
  {
    path: "/media",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/media/MediaContainer"))),
    isAccessible: FAC.mediaAllowed
  },
  // Start listings //
  ////////////////
  {
    path: "/listings/accuracy",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/listings/pages/AccuracyReportPage"))),
    isAccessible: FAC.listingAccuracyReportAllowed
  },
  {
    path: "/listings/summary",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/listings/pages/SummaryReportPage"))),
    isAccessible: FAC.listingsAllowed
  },
  {
    path: "/listings/listingOptimization",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/listings/pages/OptimizationsReportPage"))),
    isAccessible: FAC.listingOptimizationAllowed
  },
  {
    path: "/listings/missingListings",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/listings/pages/MissingReportPage"))),
    isAccessible: FAC.listingMissingReportAllowed
  },
  {
    path: "/listings/duplicateListings",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/listings/pages/DuplicatesReportPage"))),
    isAccessible: FAC.listingDuplicatesReportAllowed
  },
  {
    path: "/listings/searchAnalyticsReport",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/listings/pages/SearchAnalyticsReportPage"))),
    isAccessible: FAC.listingSearchAnalyticsReportAllowed
  },
  {
    path: "/listings/searchAnalyticsStoreComparison",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/listings/pages/SearchAnalyticsStoreComparisonReportPage"))),
    isAccessible: FAC.listingSearchAnalyticsReportAllowed
  },
  {
    path: "/listings/localPagesReport",
    component: loadableWithFilters(React.lazy(() => import("../../admin/manageTemplates/LocalPagesReport"))),
    isAccessible: FAC.localPagesAllowed

  },
  // end listings //
  ////////////////
  {
    path: "/rankings/avgRankings",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/rankings/AverageRankingContainer"))),
    isAccessible: FAC.rankingsAllowed
  },
  {
    path: "/rankings/page1Rankings",
    component: loadableWithFilters(React.lazy(() => import("../../../modules/rankings/Page1RankingContainer"))),
    isAccessible: FAC.rankingsAllowed
  },
  {
    path: "/reviewBuilder",
    component: loadable(React.lazy(() => import("../../../modules/reviewBuilder/ReviewBuilderPage"))),
    isAccessible: FAC.reviewBuilderAllowed
  },
  {
    path: "/workflow2/settings",
    component: loadable(React.lazy(() => import("../../workflow/pages/WorkflowSettings"))),
    eventName: "Task Settings",
    isAccessible: FAC.workflowAdminAllowed
  },
  {
    path: "/workflow2/taskReport",
    component: loadable(React.lazy(() => import("../../workflow/pages/WorkflowTaskReport"))),
    eventName: "Task Report",
    isAccessible: FAC.workflowAllowed
  },
  {
    path: "/workflow2/userReport",
    component: loadable(React.lazy(() => import("../../workflow/pages/WorkflowUserReport"))),
    eventName: "Task User Report",
    isAccessible: FAC.workflowAllowed
  },
  {
    path: "/workflow2",
    component: loadableWithFilters(React.lazy(() => import("../../workflow/pages/WorkflowTasks"))),
    eventName: "Task Report",
    isAccessible: FAC.workflowAllowed
  },
  {
    path: "/scores/summary",
    component: ClientDashboardAppPage,
    isAccessible: FAC.scoringAllowed
  },
  {
    path: "/scores/storeComparison/locations",
    component: ClientDashboardAppPage,
    isAccessible: FAC.scoringStoreComparisonAllowed
  },
  {
    path: "/scores/storeComparison/groups",
    component: ClientDashboardAppPage,
    isAccessible: FAC.scoringStoreComparisonAllowed
  },
  {
    path: "/scores/manageScoringRedirect",
    component: () => <Redirect to="/admin/v2/manageScoring" />,
    isAccessible: FAC.scoreManagementAllowed
  },
  {
    path: "/emailAlerts/manage",
    component: loadable(React.lazy(() => import("../../emails/EmailAlertsPage"))),
    isAccessible: FAC.emailAlertsAllowed,
    drillInPages: [
      {
        path: "/emailAlerts/manage/add",
        component: loadable(React.lazy(() => import("../../emails/AddEmailAlertsPage"))),
        isAccessible: FAC.emailAlertsAllowed
      },
      {
        path: "/emailAlerts/manage/:alertId/edit",
        component: loadable(React.lazy(() => import("../../emails/EditEmailAlertsPage"))),
        isAccessible: FAC.emailAlertsAllowed
      },
      {
        path: "/emailAlerts/manage/:alertId/copy",
        component: loadable(React.lazy(() => import("../../emails/CopyEmailAlertsPage"))),
        isAccessible: FAC.emailAlertsAllowed
      }
    ]
  },
  //////////////////////
  // start analytics //
  {
    path: "/analytics/select-template",
    component: loadable(React.lazy(() => import("../../insights"))),
    drillInPages: [
      {
        path: "/analytics/create/:templateId",
        component: loadable(React.lazy(() => import("../../insights")))
      },
    ]
  },
  {
    path: "/analytics/:reportId/:viewMode(edit|view)",
    component: loadable(React.lazy(() => import("../../insights")))
  },
  {
    path: "/analytics/create-component",
    component: loadable(React.lazy(() => import("../../insights")))
  },
  {
    path: "/analytics/reports/mine",
    component: loadable(React.lazy(() => import("../../insights"))),
    isAccessible: FAC.analyticsStudioAllowed,
  },
  {
    path: "/analytics/reports/shared",
    component: loadable(React.lazy(() => import("../../insights"))),
    isAccessible: FAC.analyticsStudioAllowed,
  },
  {
    path: "/analytics/query-builder",
    component: loadable(React.lazy(() => import("../../insights"))),
    isAccessible: FAC.analyticsStudioAllowed,
    drillInPages: [
      {
        path: "/analytics/list-data-source",
        component: loadable(React.lazy(() => import("../../insights")))
      },
      {
        path: "/analytics/create-data-source",
        component: loadable(React.lazy(() => import("../../insights")))
      },
      {
        path: "/analytics/edit-data-source/:dataSourceId",
        component: loadable(React.lazy(() => import("../../insights")))
      },
    ]
  },
  {
    path: "/analytics/bi-integration",
    component: loadable(React.lazy(() => import("../../insights"))),
    isAccessible: FAC.analyticsStudioAllowed
  },
  // end analytics //
  //////////////////
  {
    path: "/admin/v2/user",
    component: ClientDashboardAppPage
  },
  {
    path: "/admin/v2/users",
    component: ClientDashboardAppPage,
    isAccessible: FAC.userManagementAllowed
  },
  {
    path: "/admin/v2/groups",
    component: ClientDashboardAppPage,
    isAccessible: FAC.groupManagementAllowed
  },
  {
    path: "/admin/v2/manageScoring",
    component: ClientDashboardAppPage,
    isAccessible: FAC.scoreManagementAllowed
  },
  {
    path: "/admin/v2/locations",
    component: ClientDashboardAppPage,
    isAccessible: FAC.locationManagementAllowed,
    drillInPages: [
      {
        path: "/admin/v2/manageLocationsNew",
        component: ClientDashboardAppPage,
        isAccessible: FAC.locationManagementAllowed
      }
    ]
  },
  {
    path: "/admin/v2/accounts",
    component: ClientDashboardAppPage,
    isAccessible: FAC.accountManagementAllowed
  },
  {
    path: "/admin/v2/brand",
    component: ClientDashboardAppPage,
    isAccessible: FAC.brandManagementAllowed
  },
  {
    path: "/admin/v2/userRoles",
    component: ClientDashboardAppPage,
    isAccessible: FAC.roleManagementAllowed
  },
  {
    path: "/chatmeterAdminRedirect",
    component: () => <Redirect to="/chatmeterAdmin" />,
    isAccessible: FAC.isGlobalAdmin
  },
  {
    path: "/admin/v2/customProviders",
    component: ClientDashboardAppPage,
    isAccessible: FAC.userManagementAllowed
  },
  {
    path: "/admin/v2/credentials",
    component: ClientDashboardAppPage,
    isAccessible: FAC.credentialManagementAllowed
  },
  {
    path: "/admin/v2/upload",
    component: ClientDashboardAppPage,
    isAccessible: FAC.uploadToolAllowed
  },
  {
    path: "/admin/v2/manageTemplates",
    component: ManageTemplatesList,
    isAccessible: FAC.isImpersonatedByGlobalAccessAdmin
  },
  {
    path: "/admin/v2/localPagesUploader",
    component: LocalPagesUploader,
    isAccessible: FAC.isImpersonatedByGlobalAccessAdmin
  },
  {
    path: "/admin/v2/changeLogs",
    component: ChangeLogsBrowser,
    isAccessible: FAC.isGlobalAdmin
  },
  {
    path: "/admin/v2/notificationSettings",
    component: ClientDashboardAppPage,
    isAccessible: FAC.workflowAllowed
  },
  {
    path: "/admin/v2/instantAuditsUsage",
    component: ClientDashboardAppPage,
    isAccessible: FAC.instantAuditsAllowed
  },
  {
    path: "/admin/v2/locations/searchToAdd",
    component: ClientDashboardAppPage,
    isAccessible: FAC.isImpersonatedByGlobalAccessAdmin
  },
  {
    path: "/sandbox",
    component: loadableWithFilters(React.lazy(() => import('../SandboxDemoPage')))
  },
].concat(
// various prefixes for internal chatmeter/billing admin section still in ui-router
  [
    "/chatmeterAdmin", 
    "/chatmeterAdmin/billingAccounts",
    "/listingManagement/listingManagementStats/recentLocations",
    "/chatmeterAdmin/keywords",
    "/chatmeterAdmin/verticals",
    "/healthmeter",
    "/healthmeter/listingStats/summary",
    "/yelpSubscription",
    "/adviceLocalBackSync",
    "/manageTool",

    
  ].map(path => ({
    path,
    component: ClientDashboardAppPage,
    isAccessible: FAC.isGlobalAdmin
  }))
  .concat(
    // internal chatmeter/billing admin sections with react components
    [
      {
        path: "/listingSummary",
        component: loadable(React.lazy(() => import("../../listingSummary"))),
        isAccessible: FAC.isGlobalAdmin
      },
      {
        // billing admin
        path: "/admin/listingManagement",
        component: loadable(React.lazy(() => import("../../listingManagement"))),
        isAccessible: FAC.isGlobalAdmin
      }
    ]
  )
);

/**
 * component that displays some loading state in the general shape of a page.
 * Page may be configured via props
 **/
function PageLoaderSpinner({
  showFilterSkeleton = false,
  showTitleSkeleton = false
}) {
  return <React.Fragment>
    {showFilterSkeleton && <PageFilters>
      <div className="skeleton-form-control" />
      <div className="skeleton-form-control" />
      <div className="skeleton-form-control" />
      <div className="skeleton-form-control" />
    </PageFilters>}
    <div className="mt-5"><Spinner timeout={1000} className="m-auto" size="xl" center/></div>
  </React.Fragment>
}

/**
 * constructs a react component bound to the passed in "Component" promise. For chunked async loading of pages
 * @param Component
 * @param showFiltersSkeleton - should this page have filters loading state
 * @param showTitleSkeleton - should this page have a title loading state
 * @return {*} - a functional react component constructor
 */
function loadable(Component, {
  showFiltersSkeleton = false,
  showTitleSkeleton = false
} = {}) {

  return function Loadable (props) {
    return <Suspense fallback={
    <PageLoaderSpinner
      showFilterSkeleton={showFiltersSkeleton}
      showTitleSkeleton={showTitleSkeleton}
    />}><Component {...props}/></Suspense>;
  }
}

/**
 * convenience function with all the bells and whistle options of a page included,
 * so as to not need to manually specify all the loading skeletons
 * @param Component
 * @return {*}
 */
function loadableWithFilters(Component) {
  return loadable(Component, { showFiltersSkeleton: true, showTitleSkeleton: true });
}
